// Chart chrome shared by every analytics chart: a Card with a chart/table toggle (the table is
// the accessible twin of every chart), a width-measuring hook, and the hover tooltip.

import { Card, cx } from "@ml/ui";
import { Table2, ChartColumn } from "lucide-react";
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";

export function useWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w !== undefined) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

export interface ChartCardProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Extra controls/legend shown under the title. */
  legend?: ReactNode;
  actions?: ReactNode;
  chart: ReactNode;
  table: ReactNode;
  /** Empty state replaces both views. */
  empty?: ReactNode;
  /** Previous data while refetching: held at reduced opacity, no skeleton flash. */
  refreshing?: boolean;
  className?: string;
}

export function ChartCard({ title, subtitle, legend, actions, chart, table, empty, refreshing, className }: ChartCardProps) {
  const [view, setView] = useState<"chart" | "table">("chart");
  return (
    <Card
      className={cx("an-chart", className)}
      title={title}
      subtitle={subtitle}
      actions={
        <div className="an-chart__actions">
          {actions}
          {!empty ? (
            <button
              type="button"
              className="an-viewtoggle"
              aria-pressed={view === "table"}
              onClick={() => setView((v) => (v === "chart" ? "table" : "chart"))}
            >
              {view === "chart" ? <Table2 size={14} aria-hidden="true" /> : <ChartColumn size={14} aria-hidden="true" />}
              {view === "chart" ? "Table" : "Chart"}
            </button>
          ) : null}
        </div>
      }
    >
      {empty ? (
        empty
      ) : (
        <div className={cx("an-chart__body", refreshing && "an-chart__body--refreshing")}>
          {legend && view === "chart" ? <div className="an-legend">{legend}</div> : null}
          {view === "chart" ? chart : <div className="an-tablewrap">{table}</div>}
        </div>
      )}
    </Card>
  );
}

export function LegendItem({ color, label, value, shape = "rect" }: { color: string; label: ReactNode; value?: ReactNode; shape?: "rect" | "line" }) {
  return (
    <span className="an-legend__item">
      <span className={cx("an-legend__key", shape === "line" && "an-legend__key--line")} style={{ "--key": color } as CSSProperties} aria-hidden="true" />
      <span className="an-legend__label">{label}</span>
      {value !== undefined ? <span className="an-legend__value">{value}</span> : null}
    </span>
  );
}

export interface TooltipRow {
  color?: string;
  label: ReactNode;
  value: ReactNode;
}

/** Positioned inside a `position: relative` chart container. Values lead, labels follow. */
export function ChartTooltip({ x, y, width, title, rows }: { x: number; y: number; width: number; title: ReactNode; rows: TooltipRow[] }) {
  const flip = x > width - 190;
  return (
    <div
      className="an-tooltip"
      role="status"
      aria-live="polite"
      style={{ left: flip ? undefined : x + 14, right: flip ? width - x + 14 : undefined, top: Math.max(0, y) }}
    >
      <div className="an-tooltip__title">{title}</div>
      {rows.map((r, i) => (
        <div className="an-tooltip__row" key={i}>
          {r.color ? <span className="an-tooltip__key" style={{ background: r.color }} aria-hidden="true" /> : null}
          <span className="an-tooltip__value">{r.value}</span>
          <span className="an-tooltip__label">{r.label}</span>
        </div>
      ))}
    </div>
  );
}
